// tslint:disable: max-line-length object-literal-sort-keys
import { Box, HelpPanel, Icon, Link, SpaceBetween, TextContent } from '@amzn/awsui-components-core';
import {
  ContentNode,
  TechDomainDetails,
  TechDomainDisplayNames,
  allowedInternalCodeRepoUrlPrefixes,
} from '@amzn/awscat-pattern-library-service-model';
import React from 'react';
import ContentSectionsNavigation from '../workflow-viewer/content/ContentSectionsNavigation';
import MainPageHelp from './MainPageHelp';
import { getTopServicesText } from '../utils/MetadataUtils';
import constants from '../../common/constants';
import HorizontalDivider from './HorizontalDivider';

const HelpPanelContent = {
  default: () => (
    <HelpPanel header={<Box variant="h3">APG Library</Box>}>
      <MainPageHelp />
    </HelpPanel>
  ),
  // We are currently not using code-link info pane. Just keeping for record.
  'code-link': () => (
    <HelpPanel header={<Box variant="h3">Code repository</Box>}>
      <p>Currently, we only allow code links from the following resources:</p>
      <ul>
        {allowedInternalCodeRepoUrlPrefixes.map((link) => (
          <li key={link}>
            <Link external href={link}>
              {link}
            </Link>
          </li>
        ))}
      </ul>
    </HelpPanel>
  ),
  'intended-audience': () => (
    <HelpPanel header={<Box variant="h3">Intended audience</Box>}>
      <Box variant="p" color="text-body-secondary">
        Specify whether your content is appropriate for internal or external users:
      </Box>
      <Box variant="h3" padding={{ top: 'l' }}>
        Internal
      </Box>
      <Box variant="p" color="text-body-secondary">
        Only AWS employees can access the content. It will be accessible through APG Library only.
      </Box>
      <Box variant="p" color="text-body-secondary">
        This option is recommended for content that is only intended for employees or if you would
        like to incubate your content before external publishing.
      </Box>
      <Box variant="h3" padding={{ top: 'l' }}>
        External
      </Box>
      <Box variant="p" color="text-body-secondary">
        This choice indicates that the content is appropriate for AWS employees and customers and
        that it doesn't contain any confidential information. This content will first be published
        internally in APG Library and, if selected, will also be published to the external-facing{' '}
        <Link href={constants.APG_WEBSITE_URL} target="_blank">
          AWS Prescriptive Guidance
        </Link>{' '}
        site.
      </Box>
      <Box variant="p" color="text-body-secondary">
        Not every publication will be published externally. We select content with the highest
        quality and relevance to external readers. Content that is not selected for external
        publishing will remain in the selection pool for later reprioritization.
      </Box>
    </HelpPanel>
  ),
  workload: () => (
    <HelpPanel header={<Box variant="h3">Workload</Box>}>
      <p>
        If your content involves products or technologies from Microsoft, Oracle, IBM, or SAP,
        select the company from the list.
      </p>
      <p>If the content involves open-source technologies, choose that option.</p>
      <p>
        If your content discusses products or technologies from another company (such as CAST or
        F5), choose <Box variant="strong">All other workloads</Box>.
      </p>
    </HelpPanel>
  ),
  'pattern-type': () => (
    <HelpPanel header={<Box variant="h3">Pattern type</Box>}>
      <p>
        If you’re creating a migration pattern, choose <Box variant="strong">Migration</Box>. If
        not, choose a category (cloud-native, governance, optimization, process, security and
        compliance) that best describes the use case for your pattern.
      </p>
    </HelpPanel>
  ),
  environment: () => (
    <HelpPanel header={<Box variant="h3">Environment</Box>}>
      <p>Choose one of the following:</p>
      <ul>
        <li>
          <Box variant="strong">PoC or pilot</Box>: If your pattern isn’t production-ready but has
          been tested for proof-of-concept or trial use.
        </li>
        <li>
          <Box variant="strong">Production</Box>: If your pattern is fully tested and
          production-ready.
        </li>
      </ul>
    </HelpPanel>
  ),
  source: () => (
    <HelpPanel header={<Box variant="h3">Source</Box>}>
      <p>
        Specify the product, technology, or environment that your pattern discusses migrating from.
      </p>
      <Box variant="h5">Examples:</Box>
      <ul>
        <li>On-premises SQL Server database</li>
        <li>Microsoft Azure Service Bus</li>
      </ul>
    </HelpPanel>
  ),
  target: () => (
    <HelpPanel header={<Box variant="h3">Target</Box>}>
      <p>
        Specify the product, technology, or environment that your pattern discusses migrating to.
      </p>
      <Box variant="h5">Examples:</Box>
      <ul>
        <li>Amazon RDS for SQL Server</li>
        <li>Amazon SQS</li>
      </ul>
    </HelpPanel>
  ),
  rtype: () => (
    <HelpPanel header={<Box variant="h3">R type</Box>}>
      <p>Choose the migration or cloud adoption strategy that your pattern covers:</p>
      <ul>
        <li>
          <Box variant="strong">Rehost</Box>: Move to the AWS Cloud without making any changes to
          take advantage of cloud capabilities.
        </li>
        <li>
          <Box variant="strong">Replatform</Box>: Move to the AWS Cloud, and introduce some level of
          optimization to take advantage of cloud capabilities.
        </li>
        <li>
          <Box variant="strong">Re-architect</Box>: Move to the AWS Cloud, and modify application
          architecture to take full advantage of cloud-native features.
        </li>
        <li>
          <Box variant="strong">Relocate</Box>: Lift and shift infrastructure to AWS at the
          hypervisor level (typically used for VMware).
        </li>
        <li>
          <Box variant="strong">Repurchase</Box>: Replace application, potentially to a new
          cloud-native application or SaaS platform.
        </li>
        <li>
          <Box variant="strong">Retire</Box>: Retire and decommission application after rehost or
          replatform.
        </li>
        <li>
          <Box variant="strong">N/A</Box>: Choose this option if your pattern doesn’t focus on a
          specific migration strategy.
        </li>
      </ul>
    </HelpPanel>
  ),
  technologies: () => (
    <HelpPanel header={<Box variant="h3">Technologies</Box>}>
      <p>List of technologies along with the respective recommended topics</p>
      <ul>
        {[...TechDomainDetails.entries()]
          .map((domainDetails) => {
            const [domain, details] = domainDetails;
            return (
              <li key={domain}>
                <Box variant="strong">{TechDomainDisplayNames.get(domain)}</Box>
                <div>{details.topics}</div>
                {details.services ? (
                  <div>{'Services: ' + getTopServicesText(details.services)}</div>
                ) : null}
              </li>
            );
          })
          // @ts-expect-error TS7053 - strict migration
          .sort((domain1, domain2) => domain1[0] - domain2[0])}
      </ul>
    </HelpPanel>
  ),
  'content-request-creation': () => (
    <HelpPanel header={<Box variant="h3">Create suggestion</Box>}>
      <p>
        Do you have an idea for a strategy, guide, or pattern that will benefit internal users, AWS
        Partners, or external customers? If so, you can add it as a suggestion. Type in the details,
        including a title, a description, and information about the use case scenarios and AWS
        services that it would highlight.
      </p>
      <p>
        When you type in the title, APG Library search will look for similar content, so you can
        check to see if your topic is already covered in APG Library. If you have an idea that
        extends an existing strategy, guide, or pattern, please collaborate with the author instead
        of adding a new suggestion.
      </p>
      <p>
        If you want to write the content yourself or you want to invite a specific author to write
        it, use the <Box variant="strong">Assign to author</Box> field to assign it accordingly. If
        not, leave that field blank, and your suggestion will be available to all APG Library users
        for self-assignment.
      </p>
    </HelpPanel>
  ),
  'content-href': () => (
    <HelpPanel header={<Box variant="h3">Href Rules</Box>}>
      <p>
        <Box variant="strong">Home page (Pathslug):</Box>
      </p>
      <ul>
        <li>All lowercase</li>
        <li>Must be alphanumerical - no underscores, periods, or special characters</li>
        <li>Must be unique within APG</li>
        <li>
          Pathslugs for strategies should always start with “strategy-“ (QuickSight report
          requirement)
        </li>
        <li>
          <p>
            Will create the URL:
            https://docs.aws.amazon.com/prescriptive-guidance/latest/&lt;pathslug&gt;/welcome.html
          </p>
          <p>
            For example:{' '}
            <a
              href="https://docs.aws.amazon.com/prescriptive-guidance/latest/ml-production-ready-pipelines/welcome.html"
              target="_blank"
            >
              https://docs.aws.amazon.com/prescriptive-guidance/latest/ml-production-ready-pipelines/welcome.html
            </a>
          </p>
        </li>
      </ul>
      <hr />
      <p>
        <Box variant="strong">Pages (Page ID):</Box>
      </p>
      <ul>
        <li>All lowercase</li>
        <li>Must be alphanumerical - no underscores, periods, or special characters</li>
        <li>Must be unique within a strategy or guide</li>
        <li>
          <p>
            Will create the URL:
            https://docs.aws.amazon.com/prescriptive-guidance/latest/&lt;pathslug&gt;/&lt;page-id&gt;.html
          </p>
          <p>
            For example:{' '}
            <a
              href="https://docs.aws.amazon.com/prescriptive-guidance/latest/ml-production-ready-pipelines/step2.html"
              target="_blank"
            >
              https://docs.aws.amazon.com/prescriptive-guidance/latest/ml-production-ready-pipelines/step2.html
            </a>
          </p>
        </li>
      </ul>
      <hr />
      <p>
        <Box variant="strong">Sections (Section ID):</Box>
      </p>
      <ul>
        <li>All lowercase</li>
        <li>Must be alphanumerical - no underscores, periods, or special characters</li>
        <li>Must be unique within a page</li>
        <li>
          <p>
            Will create the URL:
            https://docs.aws.amazon.com/prescriptive-guidance/latest/&lt;pathslug&gt;/&lt;page-id&gt;.html#&lt;section-id&gt;
          </p>
          <p>
            For example:{' '}
            <a
              href="https://docs.aws.amazon.com/prescriptive-guidance/latest/ml-production-ready-pipelines/step2.html#unit-test"
              target="_blank"
            >
              https://docs.aws.amazon.com/prescriptive-guidance/latest/ml-production-ready-pipelines/step2.html#unit-test
            </a>
          </p>
        </li>
      </ul>
    </HelpPanel>
  ),
  'content-page-sections': (currentPage: ContentNode) => (
    <HelpPanel header={<Box variant="h3">On this page</Box>}>
      <ContentSectionsNavigation contentSections={currentPage?.contentSections} />
    </HelpPanel>
  ),
  'content-titles': () => (
    <HelpPanel header={<Box variant="h3">Titles</Box>}>
      <Box variant="h3">Pattern titles</Box>
      <Box variant="h4" padding={{ top: 'l' }}>
        Do and don'ts
      </Box>
      <ul>
        <li>
          <Box variant="strong">Do</Box> start with an imperative verb (like deploy).
        </li>
        <li>
          <Box variant="strong">Do</Box> target 50-80 characters for a long title.
        </li>
        <li>
          <Box variant="strong">Do</Box> target 40-60 characters for a short title.
        </li>
        <li>
          <Box variant="strong">Do</Box> use keywords that readers are likely to search for.
        </li>
      </ul>
      <Box variant="h4">Example</Box>
      <ul>
        <li>(Long) Migrate IIS-hosted applications to Amazon EC2 by using appcmd.exe</li>
        <li>(Short) Migrate applications hosted on IIS to Amazon EC2</li>
      </ul>

      <HorizontalDivider margin="l" />

      <Box variant="h3">Guide titles</Box>
      <Box variant="h4" padding={{ top: 'l' }}>
        Do and don'ts
      </Box>
      <ul>
        <li>
          <Box variant="strong">Do</Box> start the title with an -ing verb (like deploying) or a
          noun.
        </li>
        <li>
          <Box variant="strong">Do</Box> target 50-80 characters for a long title.
        </li>
        <li>
          <Box variant="strong">Do</Box> target 40-60 characters for a short title.
        </li>
        <li>
          <Box variant="strong">Do</Box> use keywords that readers are likely to search for.
        </li>
      </ul>
      <Box variant="h4">Example</Box>
      <ul>
        <li>(Long) Decomposing legacy mainframe applications by using AWS Transform</li>
        <li>(Short) Decomposing mainframe applications using AWS Transform</li>
      </ul>

      <HorizontalDivider margin="l" />

      <hr />
    </HelpPanel>
  ),
  'content-overview': (currentPage: ContentNode) => (
    <HelpPanel header={<Box variant="h3">Overview</Box>}>
      <p>
        The overview is used in multiple places. It is used as a content description on the APG home
        page cards. It is also used as an HTML page abstract, which improves content discoverability
        and SEO.
      </p>

      <Box variant="h3">Example</Box>

      <p>
        Follow these five recommendations to provide high availability (HA) and resiliency for your
        Kubernetes microservices applications on AWS.
      </p>

      <HorizontalDivider margin="l" />

      <hr />
    </HelpPanel>
  ),
  'content-problem-solved': (currentPage: ContentNode) => (
    <HelpPanel header={<Box variant="h3">Problem solved</Box>}>
      <p>
        The real-world use case that you describe here helps us understand how relevant your content
        is to readers.
      </p>
      <p>
        This information is used for topic pre-approval and helps us prioritize content for external
        publishing.
      </p>

      <Box variant="h3">Example</Box>

      <p>
        This pattern solves the challenge of deploying and managing a highly available, distributed
        SQL database (CockroachDB) on Kubernetes infrastructure. It provides automated IaC
        deployment using Terraform to create a fault-tolerant, multi-node CockroachDB cluster on
        Amazon EKS with proper networking, security, and scaling capabilities.
      </p>

      <HorizontalDivider margin="l" />

      <hr />
    </HelpPanel>
  ),
  'content-proposal': (currentPage: ContentNode) => (
    <HelpPanel header={<Box variant="h3">Proposal</Box>}>
      <p>
        The proposal helps us understand, in more depth than the overview, what your content is
        about.
      </p>
      <p>
        Think of it as a content summary. Provide sufficient information to help us confirm that
        your topic is unique and relevant.
      </p>
      <p>
        This information is used for topic pre-approval and to check for duplication with other
        AWS-published content.
      </p>

      <Box variant="h3">Example</Box>

      <p>
        This pattern provides a HashiCorp Terraform module for deploying a multi-node CockroachDB
        cluster on Amazon EKS by using the CockroachDB operator. This pattern uses Amazon EKS as the
        managed Kubernetes platform and implements cert-manager for TLS-secured node communication.
        It also uses a Network Load Balancer for traffic distribution and creates CockroachDB
        StatefulSets with pods that automatically replicate data for fault tolerance and
        performance.
      </p>

      <HorizontalDivider margin="l" />

      <hr />
    </HelpPanel>
  ),
  'pattern-summary': () => (
    <HelpPanel header={<Box variant="h3">Writing an Effective Summary</Box>}>
      <TextContent>
        <p>
          The summary section should provide a clear, concise overview of your pattern. Include:
        </p>
        <ul>
          <li>
            <strong>Purpose:</strong> What problem does this pattern solve?
          </li>
          <li>
            <strong>Outcome:</strong> What will users achieve by following this pattern?
          </li>
          <li>
            <strong>Technologies:</strong> List primary AWS services and third-party tools
          </li>
          <li>
            <strong>Scope:</strong> Define what is and isn&apos;t covered
          </li>
        </ul>
        <p>
          <strong>Best Practice:</strong> Keep it under 200 words but comprehensive enough for
          readers to understand the value proposition.
        </p>
      </TextContent>
    </HelpPanel>
  ),
  'pattern-architecture': () => (
    <HelpPanel header={<Box variant="h3">Documenting Architecture</Box>}>
      <TextContent>
        <p>Provide a high-level architectural overview that helps readers understand:</p>
        <ul>
          <li>
            <strong>Components:</strong> Key services and their relationships
          </li>
          <li>
            <strong>Data Flow:</strong> How information moves through the system
          </li>
          <li>
            <strong>Integration Points:</strong> External systems and APIs
          </li>
          <li>
            <strong>Security Boundaries:</strong> Trust zones and access controls
          </li>
        </ul>
        <p>
          <strong>Tip:</strong> Include architectural diagrams when possible to visualize complex
          relationships.
        </p>
      </TextContent>
    </HelpPanel>
  ),
  'pattern-prerequisites': () => (
    <HelpPanel header={<Box variant="h3">Defining Prerequisites and Limitations</Box>}>
      <TextContent>
        <p>
          <strong>Prerequisites should include:</strong>
        </p>
        <ul>
          <li>Required AWS services and permissions</li>
          <li>Technical knowledge and skills needed</li>
          <li>Existing infrastructure requirements</li>
          <li>Third-party accounts or licenses</li>
        </ul>
        <p>
          <strong>Limitations should cover:</strong>
        </p>
        <ul>
          <li>Known constraints and boundaries</li>
          <li>Scalability considerations</li>
          <li>Regional availability restrictions</li>
          <li>Cost implications</li>
        </ul>
      </TextContent>
    </HelpPanel>
  ),
  'pattern-tools': () => (
    <HelpPanel header={<Box variant="h3">Documenting Tools and Technologies</Box>}>
      <TextContent>
        <p>List all non-AWS tools used in your pattern:</p>
        <ul>
          <li>
            <strong>Tool Name:</strong> Official name and version
          </li>
          <li>
            <strong>Purpose:</strong> Why this tool is needed
          </li>
          <li>
            <strong>Installation:</strong> How to obtain and set up
          </li>
          <li>
            <strong>Configuration:</strong> Key settings and parameters
          </li>
        </ul>
        <p>
          <strong>Consider:</strong> Whether each tool warrants its own separate pattern for
          detailed coverage.
        </p>
      </TextContent>
    </HelpPanel>
  ),
  'pattern-epics': () => (
    <HelpPanel header={<Box variant="h3">Creating Effective Epics</Box>}>
      <TextContent>
        <p>
          Epics are high-level constructs that break down your pattern into manageable
          implementation phases. They serve as organizational units that group related user stories
          and tasks.
        </p>
        <h4>What Makes a Good Epic:</h4>
        <ul>
          <li>
            <strong>Epic Title:</strong> Clear, descriptive name that summarizes the major
            functionality
          </li>
          <li>
            <strong>Epic Description:</strong> Brief overview of what will be accomplished in this
            phase
          </li>
          <li>
            <strong>User Stories:</strong> 3-8 specific features written from the user&apos;s
            perspective
          </li>
          <li>
            <strong>Acceptance Criteria:</strong> Measurable conditions that define when the epic is
            complete
          </li>
          <li>
            <strong>Dependencies:</strong> Prerequisites or relationships with other epics
          </li>
        </ul>
        <h4>Epic Structure Guidelines:</h4>
        <ul>
          <li>
            <strong>Scope:</strong> Large enough to deliver meaningful value, small enough to
            complete in 2-4 sprints
          </li>
          <li>
            <strong>Independence:</strong> Should be able to be developed and deployed independently
            when possible
          </li>
          <li>
            <strong>Value-focused:</strong> Each epic should deliver clear business or user value
          </li>
          <li>
            <strong>Testable:</strong> Include clear success criteria and testing approaches
          </li>
        </ul>
        <h4>Best Practices:</h4>
        <ul>
          <li>
            Start with one comprehensive epic, then break into smaller ones as complexity becomes
            clear
          </li>
          <li>Order epics by priority and dependencies - foundational components first</li>
          <li>
            Include both functional requirements (features) and non-functional requirements
            (performance, security)
          </li>
          <li>Consider the user journey and group related functionality together</li>
          <li>Leave room for technical debt and refactoring in later epics</li>
        </ul>
        <p>
          <strong>Example Epic Structure:</strong> &quot;Core Authentication System&quot; might
          include user stories for login, registration, password reset, and session management.
        </p>
      </TextContent>
    </HelpPanel>
  ),
  'pattern-troubleshooting': () => (
    <HelpPanel header={<Box variant="h3">Writing Troubleshooting Guides</Box>}>
      <TextContent>
        <p>Help users resolve common issues:</p>
        <ul>
          <li>
            <strong>Error Description:</strong> Exact error messages or symptoms
          </li>
          <li>
            <strong>Root Cause:</strong> Why this error occurs
          </li>
          <li>
            <strong>Solution:</strong> Step-by-step resolution
          </li>
          <li>
            <strong>Prevention:</strong> How to avoid the issue
          </li>
        </ul>
        <p>
          <strong>Organization:</strong> Group by category (deployment, runtime, configuration) for
          easy navigation.
        </p>
      </TextContent>
    </HelpPanel>
  ),
  'pattern-general': () => (
    <HelpPanel header={<Box variant="h3">Pattern Documentation Guidelines</Box>}>
      <TextContent>
        <p>Follow these general principles when documenting your pattern:</p>
        <ul>
          <li>Write for your target audience&apos;s skill level</li>
          <li>Use clear, concise language</li>
          <li>Include practical examples</li>
          <li>Test all instructions before publishing</li>
        </ul>
      </TextContent>
    </HelpPanel>
  ),
  'content-type': (currentPage: ContentNode) => (
    <HelpPanel header={<Box variant="h3">Content type</Box>}>
      <Box variant="h3">Patterns</Box>
      <Box variant="h4" padding={{ top: 'l' }}>
        Do and don'ts
      </Box>
      <ul>
        <li>
          <Box variant="strong">Do</Box> describe the use case for the implementation.
        </li>
        <li>
          <Box variant="strong">Do</Box> include requirements and limitations.
        </li>
        <li>
          <Box variant="strong">Do</Box> provide architecture diagrams that illustrate the
          implementation.
        </li>
        <li>
          <Box variant="strong">Do</Box> provide epics and stories that can be imported into a
          tracking tool.
        </li>
        <li>
          <Box variant="strong">Do</Box> provide code whenever possible.
        </li>
        <li>
          <Box variant="strong">Don't</Box> describe high-level concepts or architectural options.
        </li>
      </ul>
      <Box variant="h4" padding={{ bottom: 'm', top: 'm' }}>
        Examples
      </Box>
      <SpaceBetween size="s">
        <a
          href="https://docs.aws.amazon.com/prescriptive-guidance/latest/patterns/build-an-etl-service-pipeline-to-load-data-incrementally-from-amazon-s3-to-amazon-redshift-using-aws-glue.html"
          target="_blank"
        >
          Build an ETL service pipeline to load data incrementally from Amazon S3 to Amazon Redshift
          using AWS Glue
        </a>
        <a
          href="https://docs.aws.amazon.com/prescriptive-guidance/latest/patterns/copy-ecr-container-images-across-accounts-regions.html"
          target="_blank"
        >
          Copy Amazon ECR container images across AWS accounts and AWS Regions
        </a>
        <a
          href="https://docs.aws.amazon.com/prescriptive-guidance/latest/patterns/deploy-and-manage-a-serverless-data-lake-on-the-aws-cloud-by-using-infrastructure-as-code.html"
          target="_blank"
        >
          Deploy and manage a serverless data lake on the AWS Cloud by using infrastructure as code
        </a>
      </SpaceBetween>

      <HorizontalDivider margin="l" />

      <Box variant="h3">Guides</Box>
      <Box variant="h4" padding={{ top: 'l' }}>
        Do and don'ts
      </Box>
      <ul>
        <li>
          <Box variant="strong">Do</Box> provide strategic or directional guidance.
        </li>
        <li>
          <Box variant="strong">Do</Box> describe methodologies and frameworks that help users
          understand and implement AWS technologies.
        </li>
        <li>
          <Box variant="strong">Do</Box> provide instructions at a high level.
        </li>
        <li>
          <Box variant="strong">Do</Box> highlight best practices and use cases.
        </li>
        <li>
          <Box variant="strong">Do</Box> include details and examples.
        </li>
        <li>
          <Box variant="strong">Don't</Box> provide detailed instructions about how to perform a
          single task.
        </li>
      </ul>
      <Box variant="h4" padding={{ bottom: 'm', top: 'm' }}>
        Examples
      </Box>

      <SpaceBetween size="s">
        <a
          href="https://docs.aws.amazon.com/prescriptive-guidance/latest/modernization-assessing-applications/welcome.html"
          target="_blank"
        >
          Evaluating modernization readiness for applications in the AWS Cloud
        </a>
        <a
          href="https://docs.aws.amazon.com/prescriptive-guidance/latest/modernization-phased-approach/welcome.html"
          target="_blank"
        >
          Phased approach to modernizing applications in the AWS Cloud
        </a>
        <a
          href="https://docs.aws.amazon.com/prescriptive-guidance/latest/modernization-decomposing-monoliths/welcome.html"
          target="_blank"
        >
          Decomposing monoliths into microservices
        </a>
      </SpaceBetween>

      <HorizontalDivider margin="l" />

      <hr />
    </HelpPanel>
  ),
};

export default HelpPanelContent;
